import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Stack,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Alert,
  Snackbar,
  CardContent,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Add, Visibility, FileDownloadOutlined } from "@mui/icons-material";
import CampaignBuilder from "./Campaign/CampaignBuilder";
// import * as XLSX from "xlsx";
// import { FileDownloadOutlined } from "@mui/icons-material";
import { useMediaQuery, useTheme } from '@mui/material';
import * as XLSX from 'xlsx';
import { deleteCampaign, getCampaigns, getLeadsByCampaignId } from "../api/apiService";


export default function Campaigns() {
  /* clientId from local storage */
  const clientId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('clientId')) ?? '';
    } catch {
      return '';
    }
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // true for xs and sm
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("table"); // 'table' or 'form'
  const [editData, setEditData] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [loading, setLoading] = useState(false);

  //const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(1); // 1-based index
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.ceil(rowCount / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, rowCount);

  const [leadsOpen, setLeadsOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState({ id: null, name: "" });
  const [leadRows, setLeadRows] = useState([]);
  const [leadRowCount, setLeadRowCount] = useState(0);
  const [leadPage, setLeadPage] = useState(1);
  const [leadPageSize, setLeadPageSize] = useState(5);
  const [leadLoading, setLeadLoading] = useState(false);

  const handleExportLeads = useCallback(() => {
    if (!leadRows || leadRows.length === 0){
      setSnackbar({ open: true, message: 'No records to export', severity: 'warning' });
      return;
    } 
    const exportData = leadRows.map(row => ({
      FirstName: row.firstName,
      LastName: row.lastName,
      Email: row.email,
      Company: row.company,
      JobTitle: row.jobTitle,
      Industry: row.industry,
      CompanySize: row.companySize,
      Website: row.website,
      Phone: row.phone,
      LinkedIn: row.linkedin,
      Country: row.country,
      State: row.state,
      City: row.city,
      Status: row.status,
      Score: row.score,
      Tags: (row.tags || []).join(', '),
      CreatedAt: row.createdAt,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');
    const safeName = (selectedCampaign.name || 'campaign').replace(/[^a-z0-9_-]/gi, '_');
    XLSX.writeFile(workbook, `${safeName}_leads.xlsx`);
  }, [leadRows, selectedCampaign.name]);

  /* load camppaigns on inital load or on mount */
  const fetchCampaigns = useCallback( async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const res = await getCampaigns(clientId, page, pageSize, search);
      const normalizedRows = res.data.campaigns.map((res) => ({
        ...res,
        // startDate: new Date(res.startDate?.$date || res.startDate).toLocaleDateString(),
        // startTime: new Date(res.startTime?.$date || res.startTime).toLocaleTimeString(),
      }));
      //setRows(normalizedRows);
      setRowCount(res.data.total);
      setLoading(false);
      setCampaigns(normalizedRows);
      setFilteredCampaigns(normalizedRows);
      //console.log(normalizedRows);
    } catch (error) {
      setLoading(false);
      //Not able to fetch campaign details
      setSnackbar({ open: true, message: 'Not able to fetch campaign details', severity: 'error' });
    }
  }, [clientId, page, pageSize, search]); // dependencies

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  /* search bar handler */
  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    const filtered = campaigns.filter((c) =>
      [c.name, c.description, c.location, c.audience]
        .some((field) => (field || "").toLowerCase().includes(value))
    );
    setFilteredCampaigns(filtered);
  };

  /* delete click handler */
  const handleDelete = async (id) => {
    try {
      await deleteCampaign(id);
      fetchCampaigns();
      setSnackbar({ open: true, message: 'Campaign deleted succesfully', severity: 'success' });
    } catch (error) {
      if(error.status === 400){
        setSnackbar({ open: true, message: 'Cannot delete active campaigns', severity: 'error' });
      }
      else{
        //Error deleting campaign
        setSnackbar({ open: true, message: 'Error deleting campaign', severity: 'error' });
      }
    }
  };

  /* edit click handler */
  const handleEdit = (campaign) => {
    setEditData(campaign);
    setMode("form");
  };

  /* add campaign click */
  const handleAddCampaign = () => {
    setEditData(null);
    setMode("form");
  };

  /* back button click handler on campaign builder page */
  const handleFormClose = () => {
    setMode("table");
    setEditData(null);
    fetchCampaigns();
  };

  // const handleExportCSV = () => {
  //   const exportData = campaigns.map((campaign) => ({
  //     name: campaign.name || '',
  //     description: campaign.description || '',
  //     status: campaign.status || '',
  //     audience: campaign.audience || '',
  //     location: campaign.location || '',
  //     companySize: campaign.companySize || '',
  //     startDate: campaign.startDate ? new Date(campaign.startDate).toLocaleDateString() : '',
  //     startTime: campaign.startTime ? new Date(campaign.startTime).toLocaleTimeString() : '',
  //     interval: campaign.interval === 1 ? 'Daily' : (campaign.interval === 2 ? 'Weekly' : ''),
  //     weekdays: Array.isArray(campaign.weekdays) ? campaign.weekdays.join(', ') : '',
  //   }));

  //   const worksheet = XLSX.utils.json_to_sheet(exportData);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Campaigns");
  //   XLSX.writeFile(workbook, "campaigns.csv");
  // };


  const columns = [
    { field: "name", headerName: "Name", width:220,
        renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center", 
            height: "100%",
          }}
        >
          <Typography
             onClick={() => {
                setSelectedCampaign({ id: params.row.id, name: params.row.name });
                setLeadPage(1);
                setLeadsOpen(true);
              }}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline"
            }}
          >
            {params.value}
          </Typography>
        </Box>
        )
    },
    { field: "type", headerName: "Stage", width:120,
        renderCell: (params) => (
        <Box 
        sx={{
            display: "flex",
            alignItems: "center", 
            width: '100%',
            height: '100%',
            whiteSpace: 'normal',   // allow wrapping
            wordBreak: 'break-word', // wrap long words like emails
            overflowWrap: 'break-word',
            py:1
        }}
        >
            {params.value || "Initial" }
        </Box>
        )
    },
    // { field: 'description', headerName: 'Description' , width:180,
    // renderCell: (params) => (
    //     <Box 
    //     sx={{
    //         display: 'flex',
    //         width: '100%',
    //         height: '100%',
    //         whiteSpace: 'normal',   // allow wrapping
    //         wordBreak: 'break-word', // wrap long words like emails
    //         overflowWrap: 'break-word',
    //         py:1
    //     }}
    //     >
    //         {params.value}
    //     </Box>
    // )
    // },
    { field: "audience", headerName: "Target Audience",  width:120,
            renderCell: (params) => (
      <Box 
      sx={{
          display: 'flex',
          alignItems: "center", 
          width: '100%',
          height: '100%',
          whiteSpace: 'normal',   // allow wrapping
          wordBreak: 'break-word', // wrap long words like emails
          overflowWrap: 'break-word',
          py:1
      }}
      >
          {params.value}
      </Box>
    )
    },
    { field: "location", headerName: "Location",  width:100,
            renderCell: (params) => (
      <Box 
      sx={{
          display: 'flex',
          alignItems: "center", 
          width: '100%',
          height: '100%',
          whiteSpace: 'normal',   // allow wrapping
          wordBreak: 'break-word', // wrap long words like emails
          overflowWrap: 'break-word',
          py:1
      }}
      >
          {params.value}
      </Box>
    )
    },
    // { field: "companySize", headerName: "Company Size",  width:115,
    //         renderCell: (params) => (
    //   <Box 
    //   sx={{
    //       display: 'flex',
    //       width: '100%',
    //       height: '100%',
    //       whiteSpace: 'normal',   // allow wrapping
    //       wordBreak: 'break-word', // wrap long words like emails
    //       overflowWrap: 'break-word',
    //       py:1
    //   }}
    //   >
    //       {params.value}
    //   </Box>
    // )
    // },
    // { field: "interval", headerName: "Interval",  width:120,
    //    renderCell: (params) => (
    //    <Box 
    //     sx={{
    //         display: 'flex',
    //         width: '100%',
    //         height: '100%',
    //         whiteSpace: 'normal',   // allow wrapping
    //         wordBreak: 'break-word', // wrap long words like emails
    //         overflowWrap: 'break-word',
    //         py:1
    //     }}
    //     >
    //     {params.value===1?'Daily':'Weekly'}
    //   </Box>
    // )
    // },
    { field: "weekdays", headerName: "Triggered On",  width:140,
            renderCell: (params) => (
       <Box 
        sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            whiteSpace: 'normal',   // allow wrapping
            wordBreak: 'break-word', // wrap long words like emails
            overflowWrap: 'break-word',
            py:1
        }}
        >
        {Array.isArray(params?.value) ? params.value.join(', ') : ''}
      </Box>
    )
    },
    { field: 'scheduledStartDate', headerName: 'Start Date', width:120,
      renderCell: (params) => (
      <Box 
      sx={{
          display: 'flex',
          alignItems: "center", 
          width: '100%',
          height: '100%',
          whiteSpace: 'normal',   // allow wrapping
          wordBreak: 'break-word', // wrap long words like emails
          overflowWrap: 'break-word',
          py:1
      }}
      >
          {new Date(params.value).toLocaleDateString()}
          {/* {params.value} */}
      </Box>
      )
    },
    { field: 'scheduledEndDate', headerName: 'End Date', width:120,
      renderCell: (params) => (
      <Box 
      sx={{
          display: 'flex',
          alignItems: "center", 
          width: '100%',
          height: '100%',
          whiteSpace: 'normal',   // allow wrapping
          wordBreak: 'break-word', // wrap long words like emails
          overflowWrap: 'break-word',
          py:1
      }}
      >
          {new Date(params.value).toLocaleDateString()}
          {/* {params.value} */}
      </Box>
      )
    },
    { field: 'startTime', headerName: 'Start Time', width:100,
      renderCell: (params) => ( 
        <Box 
        sx={{
            display: 'flex',
            alignItems: "center", 
            width: '100%',
            height: '100%',
            whiteSpace: 'normal',   // allow wrapping
            wordBreak: 'break-word', // wrap long words like emails
            overflowWrap: 'break-word',
            py:1
        }}
        >
          {new Date(params.value).toLocaleTimeString(undefined, {
                                                      hour: 'numeric',
                                                      minute: '2-digit',
                                                      hour12: true,
                                                    })}
          {/* {params.value} */}
        </Box>
      )
    },
    {field: 'status', headerName: 'Status', width: 110, 
    renderCell:(params) => (
        <Box sx={{ display: 'flex',py:1 , height: '100%', alignItems: "center",  }}>
            <Chip
                label={params.value}
                color={params.value==='active'?'success':params.value==='completed'?'primary':'default'}
                variant="filled"
                size="small"
            />
        </Box>
    )
    },
    { field: "actions", headerName: "Actions", width: 150,
      renderCell: (params) => (
        <Box sx={{display: 'flex',py:1 , height: '100%', alignItems: "center",  }}>
        <Stack direction="row" spacing={1}>
          <Tooltip title="View Campaign details">
          <IconButton  onClick={() => {
            setViewData(params.row);
            setViewOpen(true);
          }}>
            <Visibility fontSize="small" />
          </IconButton>
          </Tooltip>
          <Tooltip title="Edit Campaign">
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          </Tooltip>
          <Tooltip title="Delete Campaign">
          <IconButton color="red" onClick={() => {
            setDeleteId(params.row.id);
            setDeleteName(params.row.name);
            setConfirmOpen(true);
          }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
          </Tooltip>
        </Stack>
        </Box>
      ),
    },
  ];

  const leadColumns = useMemo(() => [
    { field: 'fullName', headerName: 'Full Name', width: 160,
      description: 'Full Name',
      valueGetter: (value, row) => `${row.firstName || ''} ${row.lastName || ''}`,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'email', headerName: 'Email', width: 220,
      renderCell: (params) => (
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'company', headerName: 'Company', width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'jobTitle', headerName: 'Job Title', width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'industry', headerName: 'Industry', width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'companySize', headerName: 'Company Size', width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'website', headerName: 'Website', width: 200,
      renderCell: (params) => (
        <a
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1976d2', textDecoration: 'underline' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.value}
          </Box>
        </a>
      ),
    },
    { field: 'linkedin', headerName: 'LinkedIn', width: 230,
      renderCell: (params) => (
        <a
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1976d2', textDecoration: 'underline' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.value}
          </Box>
        </a>
      ),
    },
    { field: 'tags', headerName: 'Tags', minWidth: 200, flex: 1, sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            width: '100%',
            overflowWrap: 'anywhere',
            my: 2,
          }}
        >
          {params.value?.length > 0 ? (
            params.value.map((tag, idx) => (
              <Chip key={idx} label={tag} color="primary" variant="outlined" size="small" />
            ))
          ) : (
            <Typography variant="caption" color="textDisabled">No Tags</Typography>
          )}
        </Box>
      ),
    },
    { field: 'score', headerName: 'AI Score', width: 100, type: 'number',
      renderCell: (params) => (
        <Box sx={{ my: 2.2 }}>
          <Stack gap={1} sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <Stack sx={{ flexGrow: 1 }}>
              {params.value === 0 ? 'N/A' : (
                <LinearProgress sx={{ py: 0.5, borderRadius: 1 }} variant="determinate" color={params.value > 75 ? 'green' : params.value < 65 ? 'red' : 'yellow'} value={params.value} />
              )}
            </Stack>
            <Typography variant="body2" fontWeight={500}>{params.value === 0 ? '' : params.value}</Typography>
          </Stack>
        </Box>
      )
    },
    { field: 'phone', headerName: 'phone', width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'country', headerName: 'Country', width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'state', headerName: 'State', width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'city', headerName: 'City', width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || ' - '}
        </Box>
      )
    },
    { field: 'createdAt', headerName: 'Created', width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </Box>
      )
    },
  ], []);

  const fetchCampaignLeads = useCallback(async () => {
    if (!clientId || !selectedCampaign.id) return;
    setLeadLoading(true);
    await getLeadsByCampaignId(selectedCampaign.id, clientId, leadPage, leadPageSize)
      .then((res) => {
        const normalized = res.data.leads.map((lead) => ({
          ...lead,
          createdAt: new Date(lead.createdAt?.$date || lead.createdAt).toLocaleDateString(),
          updatedAt: new Date(lead.updatedAt?.$date || lead.updatedAt).toLocaleDateString(),
        }));
        setLeadRows(normalized);
        setLeadRowCount(res.data.total);
      })
      .catch(() => {
        setLeadRows([]);
        setLeadRowCount(0);
      })
      .finally(() => setLeadLoading(false));
  }, [clientId, selectedCampaign.id, leadPage, leadPageSize]);

  useEffect(() => {
    if (leadsOpen) {
      fetchCampaignLeads();
    }
  }, [leadsOpen, fetchCampaignLeads]);

  return (
    <Box sx={{mb:2}}>
      {/* Error display via Alert and snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {mode === "table" ? (
        <>
        <Grid
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Typography variant="h5" fontWeight={600}>
            Campaigns
          </Typography>
          <Stack direction={isMobile?'column':'row'} spacing={1}>
            {/* <Button
              variant="outlined"
              startIcon={<FileDownloadOutlined />}
              sx={{ borderRadius: 2, textTransform: "none" }}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button> */}
            <Button
              size="medium"
              variant="outlined"
              startIcon={<Add />}
              sx={{ borderRadius: 2, textTransform: "none" }}
              onClick={handleAddCampaign}
            >
              Add Campaign
            </Button>
          </Stack>
        </Grid>

        <TextField
          fullWidth
          placeholder="Search Campaigns..."
          size="medium"
          value={search}
          onChange={handleSearchChange}
          sx={{ mb: 2 ,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2, 
              }
          }}
          slotProps={{ htmlInput: { maxLength: 100} }}
        />

        <Box sx={{height:510, width:'100%'}}>
        <Card elevation={0} sx={{borderRadius:2}}>
          <CardContent>

            {/* Pagination Controls */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ my: 2 }}
            >
                {/* 1. Page Info */}
                <Typography variant="body2" color="text.secondary">
                {rowCount === 0
                    ? 'No records'
                    : `Showing ${from}–${to} of ${rowCount} records`}
                </Typography>

                {/* 2. Pagination Buttons */}
                <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                size="small"
                />
            </Stack>

            <DataGrid
              sx={{height:500,borderRadius:2, p:1}}
              rows={filteredCampaigns} //rows
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              rowCount={rowCount}
              paginationMode="server"
              hideFooterPagination
              getRowHeight={() => "auto"}
              disableRowSelectionOnClick
            />

            {/* Pagination Controls */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ mt: 2 }}
            >
                {/* 1. Page Info */}
                <Typography variant="body2" color="text.secondary">
                {rowCount === 0
                    ? 'No records'
                    : `Showing ${from}–${to} of ${rowCount} records`}
                </Typography>

                {/* 2. Page Size Dropdown */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="page-size-label">Rows per page</InputLabel>
                <Select
                    labelId="page-size-label"
                    value={pageSize}
                    label="Rows per page"
                    onChange={(e) => {
                    setPageSize(e.target.value);
                    setPage(1); // Reset to first page
                    }}
                >
                    {[10, 20, 50].map((size) => (
                    <MenuItem key={size} value={size}>
                        {size}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                {/* 3. Pagination Buttons */}
                <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                size="small"
                />
            </Stack>

          </CardContent>
        </Card>
        </Box>

        </>
      ) : (
        <CampaignBuilder onClose={handleFormClose} editData={editData} />
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          }
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete  
             <strong> {deleteName} </strong> 
            from the campaign list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await handleDelete(deleteId);
              setConfirmOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* View dailog */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          }
        }}
      >
        <DialogTitle>Campaign Details</DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Box>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  {[
                    { label: "Name", value: viewData?.name || '' },
                    { label: "Audience", value: viewData?.audience || '' },
                    { label: "Location", value: viewData?.location || '' },
                    { label: "Company Size", value: viewData?.companySize || '' },
                    // Robust date handling
                    { label: "Start Date", value: viewData?.scheduledStartDate ? new Date(viewData.scheduledStartDate).toLocaleDateString() : '' },
                    { label: "End Date", value: viewData?.scheduledEndDate ? new Date(viewData.scheduledEndDate).toLocaleDateString() : '' },
                    { label: "Start Time", value: viewData?.startTime ? new Date(viewData.startTime).toLocaleTimeString(undefined, 
                      {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      }) : '' },
                    // Interval fallback for unknown interval
                    { label: "Interval", value: viewData?.interval === 1 ? "Daily" 
                                              : viewData?.interval === 2 ? "Weekly"
                                              : "" },
                    // Weekdays fallback if not array
                    { label: "Week Days", value: Array.isArray(viewData?.weekdays) ? viewData.weekdays.join(', ') : '' },
                    { label: "Status", value: viewData?.status ?? '' },  // use nullish coalescing
                    //{ label: "Follow-Ups Active", value: viewData?.isFollowUpActive ? "Yes" : "No" },
                    { label: "Emails Sent", value: viewData?.emailsSent ?? '' },
                    { label: "Emails Opened", value: viewData?.emailsOpened ?? '' },
                    { label: "Emails Clicked", value: viewData?.emailsClicked ?? '' },
                    // { label: "Replies", value: viewData?.replies ?? '' },
                    // { label: "Conversions", value: viewData?.conversions ?? '' },
                    { label: "Description", value: viewData?.description || '' },
                  ].map((item, index) => (
                    <Grid size={{xs:12, sm:12}} key={index}>
                      <Card variant='outlined' sx={{p:1}}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2">{item.value || "—"}</Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Leads by Campaign */}
      <Dialog
        open={leadsOpen}
        onClose={() => setLeadsOpen(false)}
        fullWidth
        maxWidth="lg"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Leads for {selectedCampaign.name}</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlined />}
              sx={{ borderRadius: 2, textTransform: 'none' }}
              onClick={handleExportLeads}
            >
              Export
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: 510, width: '100%' }}>
            <Card elevation={0} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ my: 2 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {leadRowCount === 0 ? 'No records' : `Showing ${(leadPage - 1) * leadPageSize + 1}–${Math.min(leadPage * leadPageSize, leadRowCount)} of ${leadRowCount} records`}
                  </Typography>
                  <Pagination
                    count={Math.ceil(leadRowCount / leadPageSize) || 0}
                    page={leadPage}
                    onChange={(e, value) => setLeadPage(value)}
                    color="primary"
                    shape="rounded"
                    size="small"
                  />
                </Stack>
                <DataGrid
                  sx={{ height: 360, borderRadius: 2 }}
                  rows={leadRows}
                  columns={leadColumns}
                  getRowId={(row) => row.id}
                  loading={leadLoading}
                  rowCount={leadRowCount}
                  paginationMode="server"
                  hideFooterPagination
                  hideFooter
                  getRowHeight={() => 'auto'}
                  localeText={{ noRowsLabel: 'No leads mapped to this campaign' }}
                />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {leadRowCount === 0 ? 'No records' : `Showing ${(leadPage - 1) * leadPageSize + 1}–${Math.min(leadPage * leadPageSize, leadRowCount)} of ${leadRowCount} records`}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="lead-page-size-label">Rows per page</InputLabel>
                    <Select
                      labelId="lead-page-size-label"
                      value={leadPageSize}
                      label="Rows per page"
                      onChange={(e) => {
                        setLeadPageSize(e.target.value);
                        setLeadPage(1);
                      }}
                    >
                      {[5, 10, 20, 50].map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={Math.ceil(leadRowCount / leadPageSize) || 0}
                    page={leadPage}
                    onChange={(e, value) => setLeadPage(value)}
                    color="primary"
                    shape="rounded"
                    size="small"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeadsOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
